/**
 * Scroll-linked position — publishes where an element sits in the viewport as
 * a custom property, and lets CSS decide what to do with it. Two flavours,
 * both read in the same pass:
 *
 *   <section data-parallax>…</section>   →  --parallax, -1 … 1
 *   <h2 data-scrub>…</h2>                →  --scrub,     0 … 1
 *
 * --parallax is -1 when the element's centre sits at the bottom edge of the
 * viewport (it is arriving), 0 when that centre is level with the middle of the
 * screen, and 1 when it has reached the top edge (it is leaving). CSS
 * multiplies it by whatever distance each layer should travel — a photo by
 * more, its caption by less — which is what makes the two read as being at
 * different depths.
 *
 * --scrub is the same idea narrowed to a band and clamped to 0…1: 0 while the
 * element's centre is still below SCRUB_START of the viewport height, 1 once it
 * has climbed past SCRUB_END, and a proportion in between. Both ends can be
 * overridden per element with data-scrub-start / data-scrub-end.
 *
 * Those overrides matter for TALL elements. The band is measured against the
 * element's CENTRE, so a section as tall as the screen has its centre half a
 * viewport behind its top edge: by the time that centre reaches the default
 * 0.92, the section is already well up the page and the entrance looks like it
 * fired late. Such a section wants a band starting past 1 — its centre still
 * below the fold, its top edge only just arriving. It is what a letter
 * cascade hangs off when it must be genuinely tied to the scroll — scroll down
 * and the letters come in one by one, scroll back up and they leave again, at
 * the speed of the wheel. Nothing is triggered and nothing latches.
 *
 * Deliberately NOT throttled through requestAnimationFrame. The handler does
 * one getBoundingClientRect and one setProperty per element, reads before it
 * writes, and is registered passive: that is cheap enough for the two or three
 * elements a page opts in. rAF would also have made it untestable in
 * environments where frames are not being produced.
 *
 * Nothing happens under prefers-reduced-motion: the property is left at 0 and
 * every layer stays where the stylesheet put it.
 */
(function () {
  // The band --scrub is measured across, as a share of the viewport height.
  // The element's centre starts the run near the bottom of the screen and
  // finishes it a little above the middle: far enough apart that the cascade
  // is a real stretch of scrolling, close enough that it is over by the time
  // the title is comfortably in view.
  var SCRUB_START = 0.92;
  var SCRUB_END = 0.42;

  var parallaxEls = [];
  var scrubEls = [];

  // Rien de tout cela sur un telephone (demande cliente du 09/09), NI SUR UNE
  // TABLETTE (demande du 10/09). Deux raisons, et la premiere suffirait : sur
  // un ecran haut et etroit, un element qui derive au defilement se lit comme
  // un defaut d'affichage plus que comme une profondeur — il n'y a pas assez
  // de largeur pour que deux calques a des vitesses differentes racontent quoi
  // que ce soit. Et le parcours au doigt est saccade la ou la molette est
  // continue : la cascade de lettres des titres de saveur, en particulier,
  // arrivait par a-coups. Les deux valent autant sur un iPad que sur un
  // telephone — c'est le doigt qui commande, pas la diagonale.
  //
  // Le seuil est celui de tout le theme : l'ordinateur commence a 1024, tout
  // ce qui est en dessous se parcourt au doigt (demande du 10/09). Ecrit en
  // pixels ici, en rem ailleurs — 63,99rem font 1023,84px, c'est la meme
  // frontiere.
  var PETIT = '(max-width: 1023px)';

  function estPetit() {
    return window.matchMedia(PETIT).matches;
  }

  // La valeur d'arrivee de chaque effet : le scrub a bout de course, le
  // parallaxe a son point mort. C'est exactement ce que la feuille de style
  // pose par defaut (`var(--scrub, 1)`), donc l'element se rend comme si le
  // script n'avait jamais tourne.
  function poserRepos() {
    var i;
    for (i = 0; i < parallaxEls.length; i++) parallaxEls[i].style.setProperty('--parallax', '0');
    for (i = 0; i < scrubEls.length; i++) scrubEls[i].style.setProperty('--scrub', '1');
  }

  function update() {
    var viewport = window.innerHeight || document.documentElement.clientHeight;
    if (!viewport) return;

    // Teste a chaque passage et non une fois pour toutes : une rotation
    // d'ecran ou un redimensionnement fait franchir le seuil dans les deux
    // sens, et les elements restent inscrits pour pouvoir reprendre.
    if (estPetit()) {
      poserRepos();
      return;
    }

    var i, rect, centre;

    for (i = 0; i < parallaxEls.length; i++) {
      rect = parallaxEls[i].getBoundingClientRect();
      // Where the element's centre is, expressed against the half-screen above
      // and below the middle. Clamped, so an element taller than the viewport
      // does not keep pushing past the ends of the range.
      centre = rect.top + rect.height / 2;
      var progress = (viewport / 2 - centre) / (viewport / 2);
      if (progress < -1) progress = -1;
      if (progress > 1) progress = 1;
      parallaxEls[i].style.setProperty('--parallax', progress.toFixed(4));
    }

    for (i = 0; i < scrubEls.length; i++) {
      var el = scrubEls[i];
      rect = el.getBoundingClientRect();
      centre = rect.top + rect.height / 2;
      var debut = parseFloat(el.dataset.scrubStart) || SCRUB_START;
      var fin = parseFloat(el.dataset.scrubEnd) || SCRUB_END;
      var scrub = (debut * viewport - centre) / ((debut - fin) * viewport);
      if (scrub < 0) scrub = 0;
      if (scrub > 1) scrub = 1;
      el.style.setProperty('--scrub', scrub.toFixed(4));
    }
  }

  function init(scope) {
    var root = scope || document;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    root.querySelectorAll('[data-parallax]').forEach(function (el) {
      if (el.dataset.parallaxInit) return;
      el.dataset.parallaxInit = '1';
      el.style.setProperty('--parallax', '0');
      if (reduced) return;
      parallaxEls.push(el);
    });

    root.querySelectorAll('[data-scrub]').forEach(function (el) {
      if (el.dataset.scrubInit) return;
      el.dataset.scrubInit = '1';
      // Fully in, until proven otherwise: no JavaScript, or a visitor who has
      // asked for less motion, and the text simply reads.
      el.style.setProperty('--scrub', '1');
      if (reduced) return;
      scrubEls.push(el);
    });

    update();
  }

  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });

  // Filet : le tout premier update() peut tomber sur une fenetre de hauteur
  // nulle (mise en page pas encore faite), auquel cas il renonce et --scrub
  // reste sur sa valeur d'amorcage, 1 — tout serait alors deja revele sans
  // jamais s'animer. On repasse une fois la page chargee et une fois les
  // polices posees, deux moments ou la hauteur est forcement connue.
  window.addEventListener('load', update);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
